import React, { useEffect, useState, useCallback, useRef } from "react";
import "./Logs.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { GetAllPatientSuperAdminwithoutId } from "../../../utils/apiCalls/patients";
import { GetAllDeviceForLogs } from "../../../utils/apiCalls/logs";
import LogsHeader from "./LogsHeader";
import LogsUserTable from "./LogsUserTable";
import LogsDeviceTable from "./LogsDeviceTable";

// Persisted across navigating to a user/device activity detail page and
// back, so the search/page isn't silently reset on return.
const SEARCH_KEY = "logsSearch";
const USER_PAGE_KEY = "logsUserPage";
const DEVICE_PAGE_KEY = "logsDevicePage";

const Logs = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(() => {
    const activeTabFromState = location.state?.activeTab;
    return activeTabFromState === "devices" ? "devices" : "users";
  });

  // Users state
  const [isLoading, setIsLoading] = useState(true);
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(() => Number(sessionStorage.getItem(USER_PAGE_KEY)) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalRows, setTotalRows] = useState(0);
  const [userFetchError, setUserFetchError] = useState("");

  // Devices state
  const [isDeviceLoading, setIsDeviceLoading] = useState(true);
  const [deviceList, setDeviceList] = useState([]);
  const [devicePage, setDevicePage] = useState(() => Number(sessionStorage.getItem(DEVICE_PAGE_KEY)) || 0);
  const [deviceRowsPerPage, setDeviceRowsPerPage] = useState(100);
  const [deviceTotalRows, setDeviceTotalRows] = useState(0);
  const [deviceFetchError, setDeviceFetchError] = useState("");

  // 🔍 Single search for both tables (used based on active tab)
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  const debounceTimeoutRef = useRef(null);
  // Restoring a persisted search on mount shouldn't reset the persisted page.
  const isFirstSearchRun = useRef(true);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem(USER_PAGE_KEY, String(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeviceChangePage = (event, newPage) => {
    setDevicePage(newPage);
    sessionStorage.setItem(DEVICE_PAGE_KEY, String(newPage));
  };

  const handleDeviceChangeRowsPerPage = (event) => {
    setDeviceRowsPerPage(parseInt(event.target.value, 10));
    setDevicePage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    // clear search when switching tabs
    setSearchQuery("");
    setDebouncedSearchQuery("");
    sessionStorage.setItem(SEARCH_KEY, "");

    if (nextTab === "users") {
      setPage(0);
      sessionStorage.setItem(USER_PAGE_KEY, "0");
    } else {
      setDevicePage(0);
      sessionStorage.setItem(DEVICE_PAGE_KEY, "0");
    }
  };

  const fetchAllPatientDetails = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsLoading(true);
      setUserFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString ?? "",
        selectedDate: null,
      };

      try {
        const res = await GetAllPatientSuperAdminwithoutId(payload);
        const { data, message, statusCode } = res;

        if (statusCode === 200) {
          setUserList(data);
          setTotalRows(res.totalRecords || data.length);
        } else if (statusCode === 204) {
          console.warn("No data found:", message);
          setUserList([]);
          setTotalRows(0);
        } else {
          console.error("Error fetching patient list:", message);
          setUserList([]);
          setTotalRows(0);
          setUserFetchError(message || "Failed to load users");
        }
      } catch (error) {
        console.error("Error fetching patient list:", error?.message);
        setUserList([]);
        setTotalRows(0);
        setUserFetchError(error?.message || "Failed to load users");
      }

      setIsLoading(false);
    },
    [],
  );

  const fetchAllDevices = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsDeviceLoading(true);
      setDeviceFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString ?? "",
        selectedDate: null,
      };

      try {
        const res = await GetAllDeviceForLogs(payload);
        const { data, message, statusCode } = res;

        if (statusCode === 200) {
          setDeviceList(data);
          setDeviceTotalRows(res.totalRecords || data.length);
        } else if (statusCode === 204) {
          console.warn("No devices found:", message);
          setDeviceList([]);
          setDeviceTotalRows(0);
        } else {
          console.error("Error fetching devices:", message);
          setDeviceList([]);
          setDeviceTotalRows(0);
          setDeviceFetchError(message || "Failed to load devices");
        }
      } catch (error) {
        console.error("Error fetching devices:", error?.message);
        setDeviceList([]);
        setDeviceTotalRows(0);
        setDeviceFetchError(error?.message || "Failed to load devices");
      }

      setIsDeviceLoading(false);
    },
    [],
  );

  // 🔁 Debounce for single search input
  useEffect(() => {
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      sessionStorage.setItem(SEARCH_KEY, searchQuery);
      if (isFirstSearchRun.current) {
        isFirstSearchRun.current = false;
      } else if (tab === "users") {
        setPage(0);
      } else {
        setDevicePage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [searchQuery, tab]);

  // Fetch user data when tab = 'users' and pagination/search changes
  useEffect(() => {
    if (tab === "users") {
      fetchAllPatientDetails(page, rowsPerPage, debouncedSearchQuery);
    }
  }, [tab, page, rowsPerPage, debouncedSearchQuery, fetchAllPatientDetails]);

  // Fetch device data when tab = 'devices' and pagination/search changes
  useEffect(() => {
    if (tab === "devices") {
      fetchAllDevices(devicePage, deviceRowsPerPage, debouncedSearchQuery);
    }
  }, [
    tab,
    devicePage,
    deviceRowsPerPage,
    debouncedSearchQuery,
    fetchAllDevices,
  ]);

  const retryUserFetch = () => {
    fetchAllPatientDetails(page, rowsPerPage, debouncedSearchQuery);
  };

  const retryDeviceFetch = () => {
    fetchAllDevices(devicePage, deviceRowsPerPage, debouncedSearchQuery);
  };

  // Navigate to user activity page
  const handleRowClick = (user) => {
    navigate(`/logs/user-activity/${user.userId}`, {
      state: { fromTab: "users" },
    });
  };

  // Navigate to device activity page
  const handleDeviceRowClick = (device) => {
    navigate(`/logs/device-activity/${encodeURIComponent(device.deviceName)}`, {
      state: { fromTab: "devices" },
    });
  };

  const searchPlaceholder =
    tab === "users" ? "Search by User Name" : "Search by Device Name";

  return (
    <div className="logs_container">
      <LogsHeader
        tab={tab}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={searchPlaceholder}
        onTabChange={handleTabChange}
      />

      {tab === "users" && (
        <LogsUserTable
          userList={userList}
          isLoading={isLoading}
          userFetchError={userFetchError}
          onRetry={retryUserFetch}
          onRowClick={handleRowClick}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {tab === "devices" && (
        <LogsDeviceTable
          deviceList={deviceList}
          isDeviceLoading={isDeviceLoading}
          deviceFetchError={deviceFetchError}
          onRetry={retryDeviceFetch}
          onRowClick={handleDeviceRowClick}
          totalRows={deviceTotalRows}
          rowsPerPage={deviceRowsPerPage}
          page={devicePage}
          onPageChange={handleDeviceChangePage}
          onRowsPerPageChange={handleDeviceChangeRowsPerPage}
        />
      )}
    </div>
  );
};

export default Logs;
